/**
 * Data access for the RPG entity (ТЗ №5, под-фаза D3 — backend D2 ``/api/rpg/*``).
 *
 * Reads are exposed as ``useApiQuery`` hooks; writes are plain functions returning the
 * server's response (the higher-level controllers in ``features/rpg`` wrap them with
 * loading/error state and trigger a refetch of the affected screen). All endpoints
 * require a Telegram-authenticated user and enforce every rule server-side.
 */
import { apiClient, useApiQuery, type QueryState } from "@shared/api";

import type {
  RpgBattleResult,
  RpgConvertResult,
  RpgInventoryItem,
  RpgMob,
  RpgProfile,
  RpgRatings,
  RpgRecipe,
  RpgShopItem,
  RpgTransferInput,
  RpgTransferResult,
  RpgWeapon,
  UpgradeStat,
} from "./model";

export const RPG_PROFILE_PATH = "/rpg/profile/";
export const RPG_MOBS_PATH = "/rpg/mobs/";
export const RPG_BATTLE_PATH = "/rpg/battle/";
export const RPG_INVENTORY_PATH = "/rpg/inventory/";
export const RPG_WEAPONS_PATH = "/rpg/weapons/";
export const RPG_WEAPON_EQUIP_PATH = "/rpg/weapons/equip/";
export const RPG_UPGRADE_WEAPON_PATH = "/rpg/upgrade-weapon/";
export const RPG_UPGRADE_STAT_PATH = "/rpg/upgrade-stat/";
export const RPG_ALCHEMY_PATH = "/rpg/alchemy/";
export const RPG_ALCHEMY_CRAFT_PATH = "/rpg/alchemy/craft/";
export const RPG_SHOP_PATH = "/rpg/shop/";
export const RPG_SHOP_BUY_PATH = "/rpg/shop/buy/";
export const RPG_CONVERT_PATH = "/rpg/convert/";
export const RPG_TRANSFER_PATH = "/rpg/transfer/";
export const RPG_RATINGS_PATH = "/rpg/ratings/";

/**
 * Fresh idempotency key for a single RPG write attempt.
 *
 * Mirrors order creation: each mutating call sends a unique ``Idempotency-Key`` header so a
 * retried or double-tapped action can never move currency / award drops twice — the server
 * enforces idempotency via Redis SETNX + a DB unique constraint (apps/rpg/services.py). One
 * key per function call. Prefers ``crypto.randomUUID`` (Mini App webviews); falls back to a
 * time+random token in older test environments.
 */
function newIdempotencyKey(): string {
  const c: Crypto | undefined =
    typeof globalThis.crypto !== "undefined" ? globalThis.crypto : undefined;
  if (c !== undefined && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  const rand = Math.random().toString(36).slice(2);
  return `rpg-${Date.now().toString(36)}-${rand}`;
}

// --------------------------------------------------------------------------- #
// Reads
// --------------------------------------------------------------------------- #
/** Hero profile + balances + derived damage. */
export function useRpgProfile(): QueryState<RpgProfile> {
  return useApiQuery<RpgProfile>(RPG_PROFILE_PATH);
}

/** Mob roster with per-hero availability (rank / daily-limit / cooldown). */
export function useRpgMobs(): QueryState<readonly RpgMob[]> {
  return useApiQuery<readonly RpgMob[]>(RPG_MOBS_PATH);
}

/** Owned inventory items (positive quantities). */
export function useRpgInventory(): QueryState<readonly RpgInventoryItem[]> {
  return useApiQuery<readonly RpgInventoryItem[]>(RPG_INVENTORY_PATH);
}

/** Weapon catalog with owned/equipped state + upgrade costs. */
export function useRpgWeapons(): QueryState<readonly RpgWeapon[]> {
  return useApiQuery<readonly RpgWeapon[]>(RPG_WEAPONS_PATH);
}

/** Alchemy recipes (inputs + result + cost + required rank). */
export function useRpgAlchemy(): QueryState<readonly RpgRecipe[]> {
  return useApiQuery<readonly RpgRecipe[]>(RPG_ALCHEMY_PATH);
}

/** In-game shop listings + purchase counts. */
export function useRpgShop(): QueryState<readonly RpgShopItem[]> {
  return useApiQuery<readonly RpgShopItem[]>(RPG_SHOP_PATH);
}

/** Leaderboards (rank / mora / primogems / wins / boss wins). */
export function useRpgRatings(): QueryState<RpgRatings> {
  return useApiQuery<RpgRatings>(RPG_RATINGS_PATH);
}

// --------------------------------------------------------------------------- #
// Writes
// --------------------------------------------------------------------------- #
/** Fight a mob → outcome + drops + refreshed profile. */
export function postBattle(mobId: number): Promise<RpgBattleResult> {
  return apiClient.post<RpgBattleResult>(RPG_BATTLE_PATH, {
    body: { mob: mobId },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Equip an owned weapon → refreshed profile. */
export function postEquipWeapon(weaponId: number): Promise<RpgProfile> {
  return apiClient.post<RpgProfile>(RPG_WEAPON_EQUIP_PATH, {
    body: { weapon: weaponId },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Spend mora + materials to level a weapon → refreshed profile. */
export function postUpgradeWeapon(weaponId: number): Promise<RpgProfile> {
  return apiClient.post<RpgProfile>(RPG_UPGRADE_WEAPON_PATH, {
    body: { weapon: weaponId },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Spend mora to raise a hero stat (hp / attack) → refreshed profile. */
export function postUpgradeStat(stat: UpgradeStat): Promise<RpgProfile> {
  return apiClient.post<RpgProfile>(RPG_UPGRADE_STAT_PATH, {
    body: { stat },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Craft an alchemy recipe → refreshed profile. */
export function postCraft(recipeId: number): Promise<RpgProfile> {
  return apiClient.post<RpgProfile>(RPG_ALCHEMY_CRAFT_PATH, {
    body: { recipe: recipeId },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Buy an in-game shop listing for mora → refreshed profile. */
export function postBuy(shopItemId: number): Promise<RpgProfile> {
  return apiClient.post<RpgProfile>(RPG_SHOP_BUY_PATH, {
    body: { shop_item: shopItemId },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Convert mora → primogems at the server rate. */
export function postConvert(mora: number): Promise<RpgConvertResult> {
  return apiClient.post<RpgConvertResult>(RPG_CONVERT_PATH, {
    body: { mora },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** Transfer mora / primogems to another player. */
export function postTransfer(input: RpgTransferInput): Promise<RpgTransferResult> {
  return apiClient.post<RpgTransferResult>(RPG_TRANSFER_PATH, {
    body: input,
    idempotencyKey: newIdempotencyKey(),
  });
}
